import { Ticket, CheckCircle, Clock, AlertTriangle, Timer } from "lucide-react";
import { useNavigate } from "react-router-dom";

function formatMinutes(minutes) {
  if (!minutes || minutes === 0) return "0m";
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours < 24) return `${hours}h ${mins}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}d ${remHours}h`;
}

const cards = [
  {
    key: "total",
    label: "Total Tickets",
    icon: Ticket,
    getValue: (s) => s.total,
    color: "#B22234",
    bg: "#fef2f2",
    accent: "#B22234",
    filterKey: null,
  },
  {
    key: "open",
    label: "Open",
    sublabel: "(all time)",
    icon: AlertTriangle,
    getValue: (s) => s.total_open_all ?? s.open,  // Use total regardless of date filter
    color: "#dc2626",
    bg: "#fef2f2",
    accent: "#dc2626",
    filterKey: "status=open",
  },
  {
    key: "closed",
    label: "Closed",
    icon: CheckCircle,
    getValue: (s) => s.closed,
    color: "#059669",
    bg: "#ecfdf5",
    accent: "#059669",
    filterKey: "status=closed",
  },
  {
    key: "waiting",
    label: "Waiting",
    sublabel: "(all time)",
    icon: Clock,
    getValue: (s) => s.total_waiting_all ?? s.waiting,  // Use total regardless of date filter
    color: "#d97706",
    bg: "#fffbeb",
    accent: "#d97706",
    filterKey: "status=waiting",
  },
  {
    key: "avg_resolution",
    label: "Avg Resolution",
    icon: Timer,
    getValue: (s) => formatMinutes(s.avg_resolution_minutes),
    color: "#6366f1",
    bg: "#eef2ff",
    accent: "#6366f1",
    filterKey: null,
    isText: true,
  },
  {
    key: "high_priority",
    label: "High Priority Open",
    icon: AlertTriangle,
    getValue: (s) => s.high_priority_open,
    color: "#dc2626",
    bg: "#fef2f2",
    accent: "#dc2626",
    filterKey: "priority=high&status=open",
  },
];

export default function KPICards({ stats }) {
  const navigate = useNavigate();

  const handleClick = (filterKey) => {
    if (!filterKey) return;
    navigate(`/tickets?${filterKey}`);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-cards-section">
      {cards.map((card, i) => {
        const Icon = card.icon;
        const value = stats ? card.getValue(stats) : "...";
        const clickable = !!card.filterKey;

        return (
          <div
            key={card.key}
            className={`corp-card ${clickable ? 'corp-card-clickable' : ''} animate-fade-slide-up relative`}
            style={{ animationDelay: `${i * 60}ms` }}
            onClick={() => clickable && handleClick(card.filterKey)}
            data-testid={`kpi-card-${card.key}`}
          >
            {/* Colored top accent bar */}
            <div className="h-1 w-full" style={{ backgroundColor: card.accent }} />

            <div className="p-5 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-xs font-semibold text-slate-500 tracking-wide uppercase">
                    {card.label}
                  </span>
                  {card.sublabel && (
                    <span className="text-[10px] text-slate-400 ml-1">{card.sublabel}</span>
                  )}
                </div>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: card.bg }}>
                  <Icon className="h-4.5 w-4.5" style={{ color: card.color }} />
                </div>
              </div>
              <div
                className={`font-bold tracking-tight ${card.isText ? 'text-xl lg:text-2xl' : 'text-3xl lg:text-4xl'}`}
                style={{ color: card.color }}
              >
                {value}
              </div>
              {clickable && (
                <p className="text-[10px] text-slate-400 mt-2 font-medium">Click to view details &rarr;</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
